import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useToast } from '../../shared/hooks/use-toast';

/**
 * Listens to browser network events and displays toast notifications
 * whenever the user disconnects/reconnects.
 *
 * Returns methods to manually control the toast display to show/hide in other
 * circumstances (eg. network errors from outside requests)
 */
export function useNetworkToast() {
    const toast = useToast();
    const toastRef = useRef<null | string>(null);
    const intl = useIntl();

    const onNetworkSuccess = useCallback(() => {
        if (toastRef.current) {
            toast.dismiss(toastRef.current);
            toastRef.current = null;
        }
    }, [toast]);

    const onNetworkError = useCallback(() => {
        if (toastRef.current) {
            return;
        }

        toastRef.current = toast.error(
            intl.formatMessage({
                id: 'network-toast.error.message',
                defaultMessage:
                    'We seem to be having trouble connecting to our servers. Please check your internet connection, this message will disappear once we re-establish the connection to our servers.',
            }),
            {
                duration: Infinity,
            },
        );
    }, [toast, intl]);

    useEffect(() => {
        const handleOnline = () => {
            onNetworkSuccess();
        };
        const handleOffline = () => {
            onNetworkError();
        };
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [onNetworkSuccess, onNetworkError]);

    return useMemo(
        () => ({
            onNetworkError,
            onNetworkSuccess,
        }),
        [onNetworkError, onNetworkSuccess],
    );
}
